import { memo, useCallback, useMemo } from 'react'

import { times } from 'lodash'

import { ListItem } from '@mui/material'

import { UserRole } from '@tabeeb/enums'
import { getUsersListForCurrentUser } from '@tabeeb/modules/../users/selectors'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  UserInfoSkeleton,
} from '@collaborate-health/uikit'

import { useSelector } from '@tabeeb/store'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { PluginUser } from '@tabeeb/modules/../users/types'

import { useContentTrainingMaterialUsers } from '../../hooks'

import ArticleReadStatusChip from '../ArticleReadStatusChip'
import CollapsibleSection from '../CollapsibleSection'
import ContentTrainingMaterialUserListItem from '../ContentTrainingMaterialUserListItem'

export type ContentTrainingMaterialUsersDialogProps = {
  open: boolean
  id?: number
  onClose: () => void
  onUpdated: () => void
}

const ContentTrainingMaterialUsersDialog: React.FC<ContentTrainingMaterialUsersDialogProps> = ({
  open,
  id,
  onClose,
  onUpdated,
}) => {
  const currentUserId = useSelector<number>(getCurrentUserId)
  const contentUsers = useSelector<PluginUser[]>(getUsersListForCurrentUser)

  const visibleUsers = useMemo(
    () =>
      contentUsers.filter(
        (user) => !user.isDeleted && user.role !== UserRole.AIUser && user.role !== UserRole.Reviewer
      ),
    [contentUsers]
  )

  const onAssigned = useCallback(
    (userIds: number[]) => {
      if (userIds.includes(currentUserId)) {
        onUpdated()
      }
    },
    [currentUserId, onUpdated]
  )

  const { loading, users, onAssignUsers } = useContentTrainingMaterialUsers({
    enabled: !!id && open,
    id,
    onError: onClose,
    onAssigned,
  })

  const assignedUsers = useMemo(() => {
    return visibleUsers.filter((user) => users.some((s) => s.UserId === user.id))
  }, [visibleUsers, users])

  const unassignedUsers = useMemo(() => {
    return visibleUsers.filter((user) => !assignedUsers.some((u) => u.id === user.id))
  }, [assignedUsers, visibleUsers])

  return (
    <Dialog open={open} fullWidth maxWidth='sm' onClose={onClose}>
      <DialogTitle>{trainingMaterialTitleFormatter.format('Manage training material users')}</DialogTitle>
      <DialogContent>
        {loading &&
          times(visibleUsers.length, (index) => (
            <ListItem key={index} disableGutters>
              <UserInfoSkeleton />
            </ListItem>
          ))}

        {!loading && assignedUsers.length > 0 && (
          <CollapsibleSection title='Assigned users'>
            {assignedUsers.map((user) => {
              const status = users.find((s) => s.UserId === user.id)

              return (
                <ContentTrainingMaterialUserListItem key={user.id} user={user}>
                  <ArticleReadStatusChip read={status?.Read ?? false} />
                </ContentTrainingMaterialUserListItem>
              )
            })}
          </CollapsibleSection>
        )}

        {!loading && unassignedUsers.length > 0 && (
          <CollapsibleSection title='Unassigned users'>
            {unassignedUsers.map((user) => (
              <ContentTrainingMaterialUserListItem key={user.id} user={user}>
                <Tooltip text={trainingMaterialTitleFormatter.format('Assign user to training material')}>
                  <Button size='small' color='primary' variant='contained' onClick={() => onAssignUsers([user.id])}>
                    Assign
                  </Button>
                </Tooltip>
              </ContentTrainingMaterialUserListItem>
            ))}
          </CollapsibleSection>
        )}
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(ContentTrainingMaterialUsersDialog)

